/**
 * Settings guide data – categories, descriptions, and related guide links.
 * Option NAMES come from settingsNames.ts (auto-generated by syncSettingsTranslations.py).
 * DESCRIPTIONS below are hand-written and safe to edit freely.
 */
import { settingsNames } from './settingsNames';
export function getOptionName(id, lang) {
    const entry = settingsNames[id];
    if (!entry)
        return id;
    return entry[lang] || entry['en'] || id;
}
/*
 * ──────────────────────────────────────────────────
 *  EDIT DESCRIPTIONS HERE
 *  Each option has a short description in EN and DE.
 *  Add more languages as needed.
 * ──────────────────────────────────────────────────
 */
export const settingsCategories = [
    {
        id: 'health',
        icon: '❤️',
        label: { en: 'Health & Body', de: 'Gesundheit & Körper' },
        options: [
            {
                id: 'HR',
                description: {
                    en: 'Shows your current heart rate from the optical sensor on the back of the watch.',
                    de: 'Zeigt die aktuelle Herzfrequenz vom optischen Sensor auf der Rückseite der Uhr.',
                },
            },
            {
                id: 'HRC',
                description: {
                    en: 'Like Heart Rate but updated every second. Only works on MIP (non-AMOLED) displays as AMOLED devices update automatically.',
                    de: 'Wie Herzfrequenz, aber jede Sekunde aktualisiert. Funktioniert nur auf MIP-Displays (nicht AMOLED), da AMOLED-Geräte automatisch aktualisieren.',
                },
            },
            {
                id: 'Ox',
                description: {
                    en: 'Blood oxygen saturation (SpO2) measured by the optical sensor. Not available on all devices.',
                    de: 'Blutsauerstoffsättigung (SpO2), gemessen vom optischen Sensor. Nicht auf allen Geräten verfügbar.',
                },
            },
            {
                id: 'BB',
                description: {
                    en: 'Garmin BodyBattery – an energy level estimate (0–100) based on stress, HRV, sleep, and activity.',
                    de: 'Garmin BodyBattery – eine Schätzung des Energielevels (0–100) basierend auf Stress, HRV, Schlaf und Aktivität.',
                },
            },
            {
                id: 'Str',
                description: {
                    en: 'Current stress level (0–100) derived from heart rate variability.',
                    de: 'Aktueller Stresslevel (0–100), abgeleitet aus der Herzfrequenzvariabilität.',
                },
            },
            {
                id: 'RR',
                description: {
                    en: 'Current breathing rate in breaths per minute.',
                    de: 'Aktuelle Atemfrequenz in Atemzügen pro Minute.',
                },
            },
            {
                id: 'TTR',
                description: {
                    en: 'Estimated recovery time in hours until you are ready for your next hard workout.',
                    de: 'Geschätzte Erholungszeit in Stunden, bis du bereit für das nächste intensive Training bist.',
                },
            },
            {
                id: 'AVRHR',
                description: {
                    en: 'Average resting heart rate over the past 7 days.',
                    de: 'Durchschnittliche Ruheherzfrequenz der letzten 7 Tage.',
                },
            },
            {
                id: 'VO2MC',
                description: {
                    en: 'Estimated VO2 Max for cycling. Requires compatible activities recorded on the device.',
                    de: 'Geschätzter VO2 Max für Radfahren. Erfordert kompatible Aktivitäten auf dem Gerät.',
                },
            },
            {
                id: 'VO2MR',
                description: {
                    en: 'Estimated VO2 Max for running.',
                    de: 'Geschätzter VO2 Max für Laufen.',
                },
            },
            {
                id: 'cTstT',
                description: {
                    en: 'Garmin Training Status – shows whether your training is productive, maintaining, overreaching, etc.',
                    de: 'Garmin Trainingsstatus – zeigt ob dein Training produktiv, erhaltend, überfordernd usw. ist.',
                },
            },
        ],
    },
    {
        id: 'activity',
        icon: '🏃',
        label: { en: 'Daily Activity', de: 'Tägliche Aktivität' },
        options: [
            {
                id: 'AMD',
                description: {
                    en: 'Total active (intensity) minutes accumulated today.',
                    de: 'Gesamte aktive (Intensitäts-) Minuten heute.',
                },
            },
            {
                id: 'AMW',
                description: {
                    en: 'Total active minutes accumulated this week.',
                    de: 'Gesamte aktive Minuten in dieser Woche.',
                },
            },
            {
                id: 'WCP',
                description: {
                    en: 'Number of wheelchair pushes today. Only available on wheelchair-enabled devices.',
                    de: 'Anzahl der Rollstuhlschübe heute. Nur auf rollstuhltauglichen Geräten verfügbar.',
                },
            },
            {
                id: 'WCD',
                description: {
                    en: 'Distance covered by wheelchair today.',
                    de: 'Heute per Rollstuhl zurückgelegte Strecke.',
                },
            },
            {
                id: 'S',
                description: {
                    en: 'Total steps taken today.',
                    de: 'Gesamte Schrittzahl heute.',
                },
            },
            {
                id: 'STSG',
                description: {
                    en: 'Remaining steps to reach your daily step goal.',
                    de: 'Verbleibende Schritte bis zum täglichen Schrittziel.',
                },
            },
            {
                id: 'Di',
                description: {
                    en: 'Total distance walked/run today (in km or mi, depends on watch unit settings).',
                    de: 'Zurückgelegte Gesamtstrecke heute (in km oder mi, abhängig von den Einheitseinstellungen).',
                },
            },
            {
                id: 'FC',
                description: {
                    en: 'Number of floors climbed today (measured by barometer).',
                    de: 'Anzahl der heute erklommenen Stockwerke (über Barometer gemessen).',
                },
            },
            {
                id: 'MC',
                description: {
                    en: 'Meters climbed today (vertical gain).',
                    de: 'Heute erklommene Höhenmeter (Aufstieg).',
                },
            },
            {
                id: 'C',
                description: {
                    en: 'Total calories burned today (active + resting metabolic rate).',
                    de: 'Gesamte heute verbrannte Kalorien (aktiv + Grundumsatz).',
                },
            },
            {
                id: 'ACTC',
                description: {
                    en: 'Only actively burned calories today (excludes resting metabolic rate).',
                    de: 'Nur aktiv verbrannte Kalorien heute (ohne Grundumsatz).',
                },
            },
            {
                id: 'be',
                description: {
                    en: 'Fun metric: how many beers (0.5l/169oz each) you have earned through today\'s calorie burn.',
                    de: 'Spaßmetrik: Wie viele Biere (je 0,5l/169oz) du dir durch den heutigen Kalorienverbrauch verdient hast.',
                },
            },
            {
                id: 'WEIG',
                description: {
                    en: 'Your weight from your Garmin Connect profile.',
                    de: 'Dein Gewicht aus deinem Garmin Connect Profil.',
                },
            },
        ],
    },
    {
        id: 'activityDistance',
        icon: '📏',
        label: { en: 'Activity History (Distance)', de: 'Aktivitätsverlauf (Distanz)' },
        categoryDescription: {
            en: 'Only available on newer devices. Only activities recorded on the watch itself are summed. On newer devices, weekly distance (run/cycle/swim) can also be provided by Garmin separately – then activities from other devices synced via the Connect app are included; this applies to weekly totals only.',
            de: 'Nur auf neueren Geräten verfügbar. Es werden nur Aktivitäten aufsummiert, die auf der Uhr selbst aufgezeichnet wurden. Auf neueren Geräten kann die wöchentliche Distanz (Lauf/Rad/Schwimmen) auch von Garmin gesondert geliefert werden – dann werden auch Aktivitäten von anderen Geräten einbezogen, sofern sie mit der Connect App synchronisiert werden; das gilt nur für die wöchentliche Summe.',
        },
        options: [
            { id: 'AWRD', description: { en: 'Running distance this week.', de: 'Laufdistanz diese Woche.' } },
            { id: 'A7RD', description: { en: 'Running distance in the last 7 days.', de: 'Laufdistanz der letzten 7 Tage.' } },
            { id: 'AMRD', description: { en: 'Running distance this month.', de: 'Laufdistanz diesen Monat.' } },
            { id: 'AWCD', description: { en: 'Cycling distance this week.', de: 'Radfahrdistanz diese Woche.' } },
            { id: 'A7CD', description: { en: 'Cycling distance in the last 7 days.', de: 'Radfahrdistanz der letzten 7 Tage.' } },
            { id: 'AMCD', description: { en: 'Cycling distance this month.', de: 'Radfahrdistanz diesen Monat.' } },
            { id: 'AWSD', description: { en: 'Swimming distance this week.', de: 'Schwimmdistanz diese Woche.' } },
            { id: 'A7SD', description: { en: 'Swimming distance in the last 7 days.', de: 'Schwimmdistanz der letzten 7 Tage.' } },
            { id: 'AMSD', description: { en: 'Swimming distance this month.', de: 'Schwimmdistanz diesen Monat.' } },
            { id: 'AWHD', description: { en: 'Hiking/Walking/Mountaineering distance this week.', de: 'Wander-/Gehen-/Bergsteigdistanz diese Woche.' } },
            { id: 'A7HD', description: { en: 'Hiking/Walking/Mountaineering distance in the last 7 days.', de: 'Wander-/Gehen-/Bergsteigdistanz der letzten 7 Tage.' } },
            { id: 'AMHD', description: { en: 'Hiking/Walking/Mountaineering distance this month.', de: 'Wander-/Gehen-/Bergsteigdistanz diesen Monat.' } },
            { id: 'A7FT', description: { en: 'Total strength workout time in the last 7 days.', de: 'Gesamte Krafttrainingszeit der letzten 7 Tage.' } },
        ],
    },
    {
        id: 'activityTime',
        icon: '⏱️',
        label: { en: 'Activity History (Time)', de: 'Aktivitätsverlauf (Zeit)' },
        categoryDescription: {
            en: 'Only available on newer devices. Only activities recorded on the watch itself are summed. On newer devices, weekly time (run/cycle/swim) can also be provided by Garmin separately – then activities from other devices synced via the Connect app are included; this applies to weekly totals only.',
            de: 'Nur auf neueren Geräten verfügbar. Es werden nur Aktivitäten aufsummiert, die auf der Uhr selbst aufgezeichnet wurden. Auf neueren Geräten kann die wöchentliche Zeit (Lauf/Rad/Schwimmen) auch von Garmin gesondert geliefert werden – dann werden auch Aktivitäten von anderen Geräten einbezogen, sofern sie mit der Connect App synchronisiert werden; das gilt nur für die wöchentliche Summe.',
        },
        options: [
            { id: 'AWRT', description: { en: 'Running time this week.', de: 'Laufzeit diese Woche.' } },
            { id: 'A7RT', description: { en: 'Running time in the last 7 days.', de: 'Laufzeit der letzten 7 Tage.' } },
            { id: 'AMRT', description: { en: 'Running time this month.', de: 'Laufzeit diesen Monat.' } },
            { id: 'AWCT', description: { en: 'Cycling time this week.', de: 'Radfahrzeit diese Woche.' } },
            { id: 'A7CT', description: { en: 'Cycling time in the last 7 days.', de: 'Radfahrzeit der letzten 7 Tage.' } },
            { id: 'AMCT', description: { en: 'Cycling time this month.', de: 'Radfahrzeit diesen Monat.' } },
            { id: 'AWST', description: { en: 'Swimming time this week.', de: 'Schwimmzeit diese Woche.' } },
            { id: 'A7ST', description: { en: 'Swimming time in the last 7 days.', de: 'Schwimmzeit der letzten 7 Tage.' } },
            { id: 'AMST', description: { en: 'Swimming time this month.', de: 'Schwimmzeit diesen Monat.' } },
        ],
    },
    {
        id: 'dateTime',
        icon: '📅',
        label: { en: 'Date & Time', de: 'Datum & Uhrzeit' },
        options: [
            {
                id: 'D',
                description: {
                    en: 'Primary date display. Format is customizable in the watchface settings below the data field options.',
                    de: 'Primäre Datumsanzeige. Das Format lässt sich in den Einstellungen unterhalb der Datenfeldoptionen anpassen.',
                },
                relatedGuide: '/guides/date-format',
            },
            {
                id: 'D2',
                description: {
                    en: 'Secondary date display with a separate customizable format.',
                    de: 'Sekundäre Datumsanzeige mit eigenem anpassbarem Format.',
                },
                relatedGuide: '/guides/date-format',
            },
            {
                id: 'D12',
                description: {
                    en: 'Shows both Date 1 and Date 2 combined in a single data field slot.',
                    de: 'Zeigt Datum 1 und Datum 2 kombiniert in einem einzigen Datenfeld-Slot.',
                },
                relatedGuide: '/guides/date-format',
            },
            {
                id: 'tts',
                description: {
                    en: 'Shows a countdown to your configured sleep time or wake-up time from your Garmin Connect profile.',
                    de: 'Zeigt einen Countdown bis zu deiner konfigurierten Schlaf- oder Aufwachzeit aus deinem Garmin Connect Profil.',
                },
            },
            {
                id: 'd2do',
                description: {
                    en: 'Shows the number of days until a specific date you set in the watchface settings.',
                    de: 'Zeigt die Anzahl der Tage bis zu einem bestimmten Datum, das du in den Einstellungen festlegst.',
                },
            },
            {
                id: 'TZ2',
                description: {
                    en: 'Second timezone display. Set the UTC offset in the watchface settings.',
                    de: 'Zweite Zeitzone. Stelle den UTC-Offset in den Einstellungen ein.',
                },
                relatedGuide: '/guides/timezones',
            },
            {
                id: 'TZ3',
                description: {
                    en: 'Third timezone display. Set the UTC offset in the watchface settings.',
                    de: 'Dritte Zeitzone. Stelle den UTC-Offset in den Einstellungen ein.',
                },
                relatedGuide: '/guides/timezones',
            },
            {
                id: 'net',
                description: {
                    en: 'Shows the time of your next calendar event synced from your phone.',
                    de: 'Zeigt die Uhrzeit deines nächsten Kalender-Events, das vom Telefon synchronisiert wird.',
                },
            },
            {
                id: 'sn',
                description: {
                    en: 'Shows the notification icon and the number of unread notifications from your phone.',
                    de: 'Zeigt das Benachrichtigungssymbol und die Anzahl ungelesener Benachrichtigungen vom Telefon.',
                },
            },
        ],
    },
    {
        id: 'battery',
        icon: '🔋',
        label: { en: 'Battery & Sensors', de: 'Batterie & Sensoren' },
        options: [
            {
                id: 'B',
                description: {
                    en: 'Current battery level in percent.',
                    de: 'Aktueller Batteriestand in Prozent.',
                },
            },
            {
                id: 'BiDT',
                description: {
                    en: 'Estimated battery life remaining in days. Not supported on all devices.',
                    de: 'Geschätzte verbleibende Batterielaufzeit in Tagen. Nicht auf allen Geräten unterstützt.',
                },
            },
            {
                id: 'STT',
                description: {
                    en: 'Temperature from the internal sensor of the watch. Note: this is affected by body heat and is not ambient temperature.',
                    de: 'Temperatur vom internen Sensor der Uhr. Hinweis: Wird von der Körperwärme beeinflusst und ist nicht die Umgebungstemperatur.',
                },
            },
            {
                id: 'So',
                description: {
                    en: 'Current solar charging intensity (0–100%). Only available on watches with a solar panel.',
                    de: 'Aktuelle Solar-Ladeintensität (0–100%). Nur auf Uhren mit Solarpanel verfügbar.',
                },
            },
            {
                id: 'SoC',
                description: {
                    en: 'Like Solar Intensity but updated every second. Only works on MIP displays.',
                    de: 'Wie Solar Intensität, aber jede Sekunde aktualisiert. Funktioniert nur auf MIP-Displays.',
                },
            },
        ],
    },
    {
        id: 'altitude',
        icon: '⛰️',
        label: { en: 'Altitude & Pressure', de: 'Höhe & Druck' },
        options: [
            {
                id: 'HS',
                description: {
                    en: 'Current altitude above sea level measured by the barometric altimeter.',
                    de: 'Aktuelle Höhe über dem Meeresspiegel, gemessen vom barometrischen Höhenmesser.',
                },
            },
            {
                id: 'Pr',
                description: {
                    en: 'Barometric air pressure (sea-level corrected).',
                    de: 'Barometrischer Luftdruck (auf Meereshöhe korrigiert).',
                },
            },
            {
                id: 'APr',
                description: {
                    en: 'Raw ambient barometric pressure (not corrected for altitude).',
                    de: 'Roher Umgebungsdruck (nicht höhenkorrigiert).',
                },
            },
        ],
    },
    {
        id: 'weather',
        icon: '⛅',
        label: { en: 'Weather', de: 'Wetter' },
        options: [
            {
                id: 'CW',
                description: {
                    en: 'Current temperature from the configured weather provider.',
                    de: 'Aktuelle Temperatur vom konfigurierten Wetteranbieter.',
                },
                relatedGuide: '/guides/weather',
            },
            {
                id: 'FLT',
                description: {
                    en: '"Feels like" temperature accounting for wind and humidity.',
                    de: 'Gefühlte Temperatur unter Berücksichtigung von Wind und Luftfeuchtigkeit.',
                },
                relatedGuide: '/guides/weather',
            },
            {
                id: 'MMT',
                description: {
                    en: 'Today\'s minimum and maximum temperature.',
                    de: 'Heutige Mindest- und Höchsttemperatur.',
                },
                relatedGuide: '/guides/weather',
            },
            {
                id: 'CW2',
                description: {
                    en: 'Current wind speed and direction.',
                    de: 'Aktuelle Windgeschwindigkeit und -richtung.',
                },
                relatedGuide: '/guides/weather',
            },
            {
                id: 'WH',
                description: {
                    en: 'Current relative humidity percentage.',
                    de: 'Aktuelle relative Luftfeuchtigkeit in Prozent.',
                },
                relatedGuide: '/guides/weather',
            },
            {
                id: 'WPC',
                description: {
                    en: 'Precipitation chance in percent.',
                    de: 'Regenwahrscheinlichkeit in Prozent.',
                },
                relatedGuide: '/guides/weather',
            },
            {
                id: 'UV',
                description: {
                    en: 'UV index. May require a specific weather provider or API plan.',
                    de: 'UV-Index. Kann je nach Wetteranbieter einen bestimmten API-Plan erfordern.',
                },
                relatedGuide: '/guides/weather',
            },
            {
                id: 'DP',
                description: {
                    en: 'Dew point temperature.',
                    de: 'Taupunkttemperatur.',
                },
                relatedGuide: '/guides/weather',
            },
            {
                id: 'WDL',
                description: {
                    en: 'Name of the weather observation station/location.',
                    de: 'Name der Wetterbeobachtungsstation / des Standorts.',
                },
            },
            {
                id: 'WDT',
                description: {
                    en: 'Time of the last weather data update.',
                    de: 'Zeitpunkt der letzten Wetterdatenaktualisierung.',
                },
            },
            {
                id: 'WDD',
                description: {
                    en: 'Weather description text (e.g. "Partly Cloudy"). Only available with OpenWeatherMap.',
                    de: 'Wetterbeschreibungstext (z.B. „Teilweise bewölkt"). Nur mit OpenWeatherMap verfügbar.',
                },
                relatedGuide: '/guides/weather',
            },
        ],
    },
    {
        id: 'sun',
        icon: '☀️',
        label: { en: 'Sun & Daylight', de: 'Sonne & Tageslicht' },
        options: [
            { id: 'NGH', description: { en: 'Time of the next golden hour (warm sunlight ideal for photography).', de: 'Zeitpunkt der nächsten Goldenen Stunde.' }, relatedGuide: '/guides/location' },
            { id: 'NSE', description: { en: 'Time of the next sunrise or sunset.', de: 'Zeitpunkt des nächsten Sonnenauf- oder -untergangs.' }, relatedGuide: '/guides/location' },
            { id: 'NBH', description: { en: 'Time of the next blue hour.', de: 'Zeitpunkt der nächsten Blauen Stunde.' }, relatedGuide: '/guides/location' },
            { id: 'NDD', description: { en: 'Time of the next dusk or dawn.', de: 'Zeitpunkt der nächsten Dämmerung.' }, relatedGuide: '/guides/location' },
            { id: 'NNDD', description: { en: 'Time of the next nautical dusk or dawn.', de: 'Zeitpunkt der nächsten nautischen Dämmerung.' }, relatedGuide: '/guides/location' },
            { id: 'TGH', description: { en: 'Countdown until the next golden hour.', de: 'Countdown bis zur nächsten Goldenen Stunde.' } },
            { id: 'TSE', description: { en: 'Countdown until the next sunrise or sunset.', de: 'Countdown bis zum nächsten Sonnenauf-/untergang.' } },
            { id: 'TBH', description: { en: 'Countdown until the next blue hour.', de: 'Countdown bis zur nächsten Blauen Stunde.' } },
            { id: 'TDD', description: { en: 'Countdown until the next dusk or dawn.', de: 'Countdown bis zur nächsten Dämmerung.' } },
            { id: 'TNDD', description: { en: 'Countdown until the next nautical dusk or dawn.', de: 'Countdown bis zur nächsten nautischen Dämmerung.' } },
            { id: 'SR', description: { en: 'Today\'s sunrise time. Requires a GPS fix.', de: 'Heutige Sonnenaufgangszeit. Erfordert GPS-Fix.' }, relatedGuide: '/guides/location' },
            { id: 'SS', description: { en: 'Today\'s sunset time. Requires a GPS fix.', de: 'Heutige Sonnenuntergangszeit. Erfordert GPS-Fix.' }, relatedGuide: '/guides/location' },
        ],
    },
    {
        id: 'moon',
        icon: '🌓',
        label: { en: 'Moon', de: 'Mond' },
        options: [
            { id: 'MR', description: { en: 'Today\'s moonrise time.', de: 'Heutige Mondaufgangszeit.' }, relatedGuide: '/guides/location' },
            { id: 'MS', description: { en: 'Today\'s moonset time.', de: 'Heutige Monduntergangszeit.' }, relatedGuide: '/guides/location' },
            { id: 'NMR', description: { en: 'Time of the next moonrise or moonset.', de: 'Zeitpunkt des nächsten Mondauf-/untergangs.' } },
            { id: 'MP', description: { en: 'Current moon phase icon and illumination percentage.', de: 'Aktuelles Mondphasen-Icon und Belichtungsprozentsatz.' } },
            { id: 'MD', description: { en: 'Moon age – days since the last new moon.', de: 'Mondalter – Tage seit dem letzten Neumond.' } },
        ],
    },
    {
        id: 'tides',
        icon: '🌊',
        label: { en: 'Tides', de: 'Gezeiten' },
        options: [
            { id: 'NTT', description: { en: 'Time of the next tide event (high or low). Requires a Stormglass.io API key.', de: 'Zeitpunkt des nächsten Gezeitenereignisses. Erfordert einen Stormglass.io API-Schlüssel.' }, relatedGuide: '/guides/tides' },
            { id: 'NTL', description: { en: 'Time of the next low tide.', de: 'Zeitpunkt der nächsten Ebbe.' }, relatedGuide: '/guides/tides' },
            { id: 'NTH', description: { en: 'Time of the next high tide.', de: 'Zeitpunkt der nächsten Flut.' }, relatedGuide: '/guides/tides' },
        ],
    },
    {
        id: 'charts',
        icon: '📈',
        label: { en: 'Charts', de: 'Diagramme' },
        options: [
            { id: 'CHH', description: { en: 'Heart rate history chart. Only available on specific watchfaces.', de: 'Herzfrequenz-Verlaufsdiagramm. Nur bei bestimmten Watchfaces verfügbar.' } },
            { id: 'CHP', description: { en: 'Air pressure history chart.', de: 'Luftdruck-Verlaufsdiagramm.' } },
            { id: 'CHE', description: { en: 'Elevation history chart.', de: 'Höhenverlaufsdiagramm.' } },
            { id: 'CHT', description: { en: 'Internal sensor temperature history chart.', de: 'Sensortemperatur-Verlaufsdiagramm.' } },
            { id: 'CHS', description: { en: 'Stress level history chart.', de: 'Stresslevel-Verlaufsdiagramm.' } },
        ],
    },
    {
        id: 'other',
        icon: '🔧',
        label: { en: 'Other', de: 'Sonstiges' },
        options: [
            {
                id: 'N',
                description: {
                    en: 'Disables this data field slot – no data will be shown.',
                    de: 'Deaktiviert diesen Datenfeld-Slot – es werden keine Daten angezeigt.',
                },
            },
            {
                id: 'ct',
                description: {
                    en: 'Shows a custom text string you can set in the watchface settings.',
                    de: 'Zeigt einen benutzerdefinierten Text, den du in den Einstellungen festlegen kannst.',
                },
            },
            {
                id: 'MGRS',
                description: {
                    en: 'Military Grid Reference System coordinate of your current GPS position.',
                    de: 'MGRS-Koordinate (militärisches Gitternetz) deiner aktuellen GPS-Position.',
                },
                relatedGuide: '/guides/location',
            },
        ],
    },
];
